import { ConnectedPosition } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LucideX } from '@lucide/angular';

export interface AutocompleteSearchOption<T = unknown> {
  /** Unique, defined value used as both the form-control value and the @for track key. */
  value: T;
  label: string;
  sublabel?: string;
  dotColor?: string;
  isDefault?: boolean;
}

/**
 * Visual style for the trigger.
 * - `chip` (default): selected values render as chips with a dashed "+ Add" affordance.
 * - `filter`: a single compact button styled like a filter pill — `Label: value ▾`.
 * - `menu`: a compact filter pill showing just the selected label (or `filterPlaceholder`), opening a searchless option menu where the selected row is ticked.
 */
export type AutocompleteSearchVariant = 'chip' | 'filter' | 'menu';

@Component({
  selector: 'ptl-autocomplete-search',
  templateUrl: './sg-autocomplete-search.component.html',
  styleUrls: ['./sg-autocomplete-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SgAutocompleteSearchComponent),
      multi: true,
    },
  ],
})
export class SgAutocompleteSearchComponent<T = unknown> implements ControlValueAccessor {
  @Input() options: AutocompleteSearchOption<T>[] = [];
  @Input() multiSelect = false;
  @Input() label = '';
  @Input() addLabel = '+ Add';
  @Input() removeLabel = 'Remove';
  @Input() searchPlaceholder = 'Search…';
  @Input() searchAriaLabel = '';
  @Input() doneLabel = 'Done';
  @Input() selectedCountSuffix = 'selected';
  @Input() counterSuffix = '';
  @Input() counterTemplate?: (count: number) => string;
  @Input() disabled = false;
  @Input() isLoading = false;
  @Input() variant: AutocompleteSearchVariant = 'chip';
  /** Text shown in the filter-variant trigger when no value is selected (e.g. "All kinds"). */
  @Input() filterPlaceholder = '';
  /** When set, shows a "Clear all" link in the multi-select footer. Clicking it commits the empty selection and closes the panel. */
  @Input() clearAllLabel?: string;

  /** When true, `options` are already filtered by a parent (server-side search) — skip the local text filter. */
  @Input() serverSide = false;

  /** Offers a "create the typed term" action in the panel when no option matches it. */
  @Input() allowCreate = false;
  @Input() createLabel = '+ Create';

  /** Horizontal panel alignment against the trigger: 'start' (default) or 'end' (right edge aligned, opens leftwards). */
  @Input() panelAlign: 'start' | 'end' = 'start';

  @Output() selectionChange = new EventEmitter<T | T[] | null>();
  @Output() opened = new EventEmitter<void>();
  /** Emits the raw search term so a parent can drive server-side search-as-you-type. */
  @Output() searchTermChange = new EventEmitter<string>();
  /** Emits the term to create. The panel stays open so the new value can be shown as selected. */
  @Output() createRequested = new EventEmitter<string>();

  /** A real glyph rather than the `×` character, whose ink sits above the text box's centre. */
  readonly LucideX = LucideX;

  isOpen = false;
  searchText = '';
  pendingSelection: T[] = [];
  private value: T | T[] | null = null;

  private static readonly START_POSITIONS: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 },
  ];

  private static readonly END_POSITIONS: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
  ];

  private static readonly OVERLAY_POSITIONS: Record<'start' | 'end', ConnectedPosition[]> = {
    start: [...SgAutocompleteSearchComponent.START_POSITIONS, ...SgAutocompleteSearchComponent.END_POSITIONS],
    end: [...SgAutocompleteSearchComponent.END_POSITIONS, ...SgAutocompleteSearchComponent.START_POSITIONS],
  };

  get overlayPositions(): ConnectedPosition[] {
    return SgAutocompleteSearchComponent.OVERLAY_POSITIONS[this.panelAlign];
  }

  private onChange: (v: T | T[] | null) => void = () => undefined;
  private onTouched = () => undefined;

  constructor(private cdr: ChangeDetectorRef) {}

  writeValue(v: T | T[] | null): void {
    this.value = v;
    if (this.isOpen) {
      this.pendingSelection = this.valueToArray(v);
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (v: T | T[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(d: boolean): void {
    this.disabled = d;
    this.cdr.markForCheck();
  }

  get selectedOptions(): AutocompleteSearchOption<T>[] {
    const arr = this.valueToArray(this.value);
    return arr.map((v) => this.options.find((o) => o.value === v)).filter((o): o is AutocompleteSearchOption<T> => !!o);
  }

  get filterDisplayValue(): string {
    const selected = this.selectedOptions;
    if (!selected.length) {
      return this.filterPlaceholder;
    }
    if (selected.length === 1) {
      return selected[0].isDefault && this.filterPlaceholder ? this.filterPlaceholder : selected[0].label;
    }
    return `${selected.length} ${this.selectedCountSuffix}`;
  }

  get isFilterApplied(): boolean {
    const selected = this.selectedOptions;
    return selected.length > 0 && !selected.every((opt) => opt.isDefault);
  }

  get triggerAriaLabel(): string {
    const name = this.label || this.filterPlaceholder;
    return name ? `${name}: ${this.filterDisplayValue}` : this.filterDisplayValue;
  }

  get filteredOptions(): AutocompleteSearchOption<T>[] {
    // In server-side mode the parent already filters `options` for the term; re-filtering locally would
    // hide valid server matches (accents / fields not in the label) and show stale results mid-debounce.
    if (this.serverSide) return this.options;
    const q = this.searchText.trim().toLowerCase();
    if (!q) return this.options;
    return this.options.filter((o) => o.label.toLowerCase().includes(q) || (o.sublabel ?? '').toLowerCase().includes(q));
  }

  /**
   * The typed term when it matches no option, so the panel can offer to create it. Lives inside the
   * overlay on purpose: the overlay has a backdrop, so a create affordance rendered outside it would
   * only ever receive the click that dismisses the panel.
   */
  get creatableTerm(): string {
    if (!this.allowCreate || this.isLoading) return '';
    const term = this.searchText.trim();
    if (!term) return '';
    return this.options.some((o) => o.label.trim().toLowerCase() === term.toLowerCase()) ? '' : term;
  }

  get counterLabel(): string {
    const count = this.filteredOptions.length;
    if (this.counterTemplate) return this.counterTemplate(count);
    return this.counterSuffix ? `${count} ${this.counterSuffix}` : `${count}`;
  }

  isSelected(v: T): boolean {
    return this.pendingSelection.includes(v);
  }

  trackByValue = (_: number, opt: AutocompleteSearchOption<T>) => opt.value;

  toggleOpen(): void {
    if (this.disabled) return;
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    this.pendingSelection = this.valueToArray(this.value);
    this.searchText = '';
    this.isOpen = true;
    this.opened.emit();
  }

  onSearchInput(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value;
    this.searchTermChange.emit(this.searchText);
    this.cdr.markForCheck();
  }

  /** Hands the term to the parent, which creates the record and selects it; the panel stays open. */
  onCreate(): void {
    const term = this.creatableTerm;
    if (!term) return;
    this.searchText = '';
    this.searchTermChange.emit('');
    this.createRequested.emit(term);
    this.cdr.markForCheck();
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.onTouched();
  }

  toggleOption(opt: AutocompleteSearchOption<T>): void {
    if (this.multiSelect) {
      const idx = this.pendingSelection.indexOf(opt.value);
      this.pendingSelection = idx === -1 ? [...this.pendingSelection, opt.value] : this.pendingSelection.filter((_, i) => i !== idx);
      return;
    }
    this.pendingSelection = this.pendingSelection[0] === opt.value ? [] : [opt.value];
    this.commit();
    this.close();
  }

  removeChip(value: T, event: Event): void {
    event.stopPropagation();
    if (this.disabled) return;
    const base = this.isOpen ? this.pendingSelection : this.valueToArray(this.value);
    this.pendingSelection = base.filter((v) => v !== value);
    this.commit();
  }

  onDone(): void {
    this.commit();
    this.close();
  }

  onClearAll(): void {
    this.pendingSelection = [];
    this.commit();
    this.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) this.close();
  }

  private commit(): void {
    if (this.multiSelect) {
      const arr = [...this.pendingSelection];
      this.value = arr;
      this.onChange(arr);
      this.selectionChange.emit(arr);
      return;
    }
    const v = this.pendingSelection[0] ?? null;
    this.value = v;
    this.onChange(v);
    this.selectionChange.emit(v);
  }

  private valueToArray(v: T | T[] | null): T[] {
    if (v == null) return [];
    return Array.isArray(v) ? [...v] : [v];
  }
}
